import { useState, useCallback, useEffect } from 'react';
import { Heart, Sparkles, Image, X } from 'lucide-react';
import { PORTRAITS } from '@/config/valentineContent';

const STICKER_EMOJIS = ['\u2764\uFE0F', '\u2728', '\u{1F338}', '\u{1F49D}', '\u2B50', '\u{1F33A}', '\u{1F496}', '\u{1F31F}', '\u{1F339}', '\u{1F49E}'];
const ROTATIONS = [-4, 3, -2.5, 5, -3.5, 2, 0];

function PhotoCard({ photo, index, onOpen, isFeatured }) {
  const [imgError, setImgError] = useState(false);
  if (!photo) return null;

  const hasSrc = photo.src && !imgError;
  const rotation = isFeatured ? 0 : ROTATIONS[index % ROTATIONS.length];
  const sticker = STICKER_EMOJIS[index % STICKER_EMOJIS.length];

  return (
    <div className={`polaroid-card polaroid-enter ${isFeatured ? 'polaroid-featured' : ''}`}
      style={{
        animationDelay: `${index * 120}ms`,
        '--rotation': `${rotation}deg`,
      }}
      onClick={() => hasSrc && onOpen?.(index)}>
      <div className="polaroid-frame" style={{ cursor: hasSrc ? 'pointer' : 'default' }}>
        {/* Tape / sticker at top */}
        <div className="polaroid-tape" style={{ transform: `rotate(${-rotation * 0.4}deg)` }}>
          <span className="polaroid-tape-emoji">{sticker}</span>
        </div>

        {/* Photo area */}
        <div className={`polaroid-photo ${isFeatured ? 'polaroid-photo-featured' : ''}`}>
          {hasSrc ? (
            <img
              src={photo.src}
              alt={photo.caption || ''}
              onError={() => setImgError(true)}
              className="polaroid-img"
            />
          ) : (
            <div className="polaroid-placeholder">
              <Image size={24} style={{ color: 'var(--accent-light)', opacity: 0.5 }} />
            </div>
          )}
        </div>

        {/* Caption area */}
        <div className="polaroid-caption">
          <p className={`font-cursive ${isFeatured ? 'text-base' : 'text-sm'}`} style={{ color: 'var(--text-primary)' }}>
            {photo.caption || ''}
          </p>
        </div>
      </div>
    </div>
  );
}

function Lightbox({ photo, onClose }) {
  const [imgError, setImgError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [closing, setClosing] = useState(false);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => onClose?.(), 300);
  }, [onClose]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [handleClose]);

  if (!photo) return null;

  return (
    <div className={`polaroid-lightbox ${closing ? 'polaroid-lb-closing' : ''}`} onClick={handleClose}>
      <div className={`polaroid-lightbox-content ${closing ? 'polaroid-lb-content-closing' : ''}`} onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button onClick={handleClose} className="polaroid-lightbox-close"
          style={{ color: 'var(--accent-primary)', background: 'rgba(233,30,99,0.1)', border: '1px solid var(--accent-glow)', cursor: 'pointer' }}>
          <X size={20} />
        </button>

        {/* Polaroid frame in lightbox */}
        <div className="polaroid-lightbox-frame">
          {photo.src && !imgError ? (
            <img
              src={photo.src}
              alt={photo.caption || ''}
              onError={() => setImgError(true)}
              onLoad={() => setLoaded(true)}
              className={`polaroid-lightbox-img ${loaded ? 'polaroid-lb-loaded' : ''}`}
            />
          ) : (
            <div className="polaroid-lightbox-placeholder">
              <Heart size={32} style={{ color: 'var(--accent-light)' }} />
            </div>
          )}
          {photo.caption && (
            <div className="polaroid-lightbox-caption">
              <p className="font-cursive text-lg" style={{ color: 'var(--text-primary)' }}>
                {photo.caption}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PortraitGallery() {
  const photos = PORTRAITS?.photos || [];
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const openLightbox = useCallback((i) => setLightboxIndex(i), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  if (photos.length === 0) return null;

  const activePhoto = lightboxIndex !== null ? photos[lightboxIndex] : null;

  return (
    <section id="portrait-gallery" data-testid="portrait-gallery-section" className="section-romantic text-center">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-center gap-3 mb-2">
          <div className="w-12 h-[1px]" style={{ background: 'linear-gradient(90deg, transparent, var(--accent-light))' }} />
          <Sparkles size={14} style={{ color: 'var(--accent-primary)' }} />
          <div className="w-12 h-[1px]" style={{ background: 'linear-gradient(90deg, var(--accent-light), transparent)' }} />
        </div>
        <p className="font-body text-[10px] tracking-widest uppercase mb-2" style={{ color: 'var(--accent-light)' }}>
          {PORTRAITS?.sectionLabel}
        </p>
        <h2 className="font-heading text-2xl md:text-3xl mb-10" style={{ color: 'var(--text-primary)' }}>
          {PORTRAITS?.heading}
        </h2>

        {/* Grid */}
        <div className="polaroid-grid">
          {photos.map((photo, i) => (
            <PhotoCard key={photo?.id || i} photo={photo} index={i} onOpen={openLightbox} isFeatured={i === photos.length - 1} />
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {activePhoto && <Lightbox photo={activePhoto} onClose={closeLightbox} />}
    </section>
  );
}
